import { memo, useEffect, useMemo, useRef, useState } from 'react';

import axios, { AxiosProgressEvent, AxiosResponse } from 'axios';
import { ChevronDown, CircleAlert, CircleCheck, Info, UploadCloud } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { FileRejection, useDropzone } from 'react-dropzone';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { FileProgress } from '@/components/ui/file-progress';
import { Typography } from '@/components/ui/typography';
import { cn } from '@/lib/utils';
import { generateCodeVerifier } from '@/services/pkce';
import { showAlert } from '@/slice/AlertSlice';
import { Variants } from '@/types/common';
import { getAPIUrls } from '@/util/env';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

// Upload responses come back from raw axios as `unknown` at this layer; the
// feature-side consumer (BulkImportUpload) narrows to its known shape
// (UploadApiResponse / BulkUploadFileResponse) at the callsite.
export type bulkFileImportProps = {
  onFileUpload: (response: unknown) => void;
  url: string;
  setShowStep: ((v: boolean) => void) | null;
  isProperty?: boolean;
  onFileUploadForOrder: (response: unknown) => void;
  setFileUploadDisabled: (v: boolean) => void;
  fileUploadDisabled: boolean;
  setInProgress: (v: boolean) => void;
  inProgress: boolean;
  setDisabledUploadProperty: (v: boolean) => void;
  disabledUploadProperty: boolean;
};

export type fileDetails = {
  file: File;
  progress: number;
  isError: boolean;
  errorMessage: string;
  fileId: string;
  controller: AbortController;
};

export type allFileDetails = {
  fileProgress: number;
  filesInSuccess: number;
  errorProgress: number;
  filesInError: number;
};

export type fileProgressProps = {
  status: fileDetails;
  onCancel: () => void;
  isProperty?: boolean;
  inProgress?: boolean;
};

// ---------------------------------------------------------------------------
// Per-mode limits
// ---------------------------------------------------------------------------

interface ModeLimits {
  maxKb: number;
  maxRecords: number;
  helperCopy: string;
  alertEntity: 'property upload' | 'order';
  alertSection: 'Bulk property import' | 'Bulk order';
}

const PROPERTY_LIMITS: ModeLimits = {
  maxKb: 500,
  maxRecords: 5000,
  helperCopy: '500 kb maximum file size, 5000 properties maximum, .xlsx and csv files only.',
  alertEntity: 'property upload',
  alertSection: 'Bulk property import',
};

const ORDER_LIMITS: ModeLimits = {
  maxKb: 50,
  maxRecords: 1000,
  helperCopy: '50 kb maximum file size, 1000 properties maximum, .xlsx and csv files only.',
  alertEntity: 'order',
  alertSection: 'Bulk order',
};

const SPREADSHEET_MIME_TYPES = {
  'text/csv': [],
  'application/vnd.ms-excel': [],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [],
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const BulkFileImport = memo(function BulkFileImport({
  onFileUpload,
  url,
  setShowStep,
  onFileUploadForOrder,
  isProperty,
  setFileUploadDisabled,
  fileUploadDisabled,
  setInProgress,
  inProgress,
  setDisabledUploadProperty,
  disabledUploadProperty,
}: bulkFileImportProps) {
  const dispatch = useDispatch();
  const limits = isProperty ? PROPERTY_LIMITS : ORDER_LIMITS;

  const [fileStatus, setFileStatus] = useState<fileDetails[]>([]);
  const [isExpanded, setIsExpanded] = useState<boolean>(true);
  const [isBeingDragged, setIsBeingDragged] = useState<boolean>(false);
  const accordionRef = useRef<HTMLDivElement>(null);

  const { acceptedFiles, fileRejections, getRootProps, getInputProps } = useDropzone({
    accept: SPREADSHEET_MIME_TYPES,
    multiple: true,
    onDrop: (files) => {
      if (files.length > 1) {
        dispatch(
          showAlert({
            type: Variants.ERROR,
            message: 'Only one file can be uploaded at a time.',
          }),
        );
        files.splice(0);
      } else {
        files.splice(1);
      }
    },
  });

  // -----------------------------------------------------------------------
  // File processing
  // -----------------------------------------------------------------------

  const exceedsSizeLimit = (bytes: number) => Math.floor(bytes / 1000) > limits.maxKb;
  const hasInvalidExtension = (fileName: string) =>
    !(fileName.endsWith('.csv') || fileName.endsWith('.xlsx'));

  const setDisabledForMode = (disabled: boolean) => {
    if (isProperty) {
      setDisabledUploadProperty(disabled);
    } else {
      setFileUploadDisabled(disabled);
    }
  };

  useEffect(() => {
    processFiles(acceptedFiles);
  }, [acceptedFiles]);

  useEffect(() => {
    processFiles(fileRejections.map((rejection: FileRejection) => rejection.file));
  }, [fileRejections]);

  const processFiles = (incomingFiles: File[]) => {
    if (incomingFiles.length === 0) return;

    const next: fileDetails[] = [...fileStatus];
    incomingFiles.forEach((file: File) => {
      const tooLarge = exceedsSizeLimit(file.size);
      const wrongExtension = hasInvalidExtension(file.name);
      const stat: fileDetails = {
        file,
        progress: 0,
        isError: tooLarge || wrongExtension,
        errorMessage: wrongExtension
          ? 'Only .xlsx and .csv files are allowed'
          : `File size exceeds ${limits.maxKb} kb. Reduce the file size and try again.`,
        controller: new AbortController(),
        fileId: generateCodeVerifier(),
      };
      next.unshift(stat);
    });

    setFileStatus(next);
    next.forEach((stat) => {
      if (!stat.isError && stat.progress === 0) {
        handleFileUpload(stat);
      }
    });
    accordionRef.current?.scrollIntoView();
  };

  const handleFileUpload = (stat: fileDetails) => {
    setDisabledForMode(true);
    setInProgress(true);

    const formData = new FormData();
    formData.append('file', stat.file);

    axios
      .post<unknown>(getAPIUrls(url), formData, {
        signal: stat.controller.signal,
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (event: AxiosProgressEvent) => updateProgress(event, stat),
      })
      .then((response: AxiosResponse<unknown>) => {
        if (isProperty) {
          setInProgress(false);
          onFileUpload(response.data);
        } else {
          onFileUploadForOrder(response.data);
        }
      })
      .catch((error: unknown) => {
        setDisabledForMode(false);
        setInProgress(false);
        // DV-2976: show the reason the server gave. It was already read here and then dropped in
        // favour of one fixed string for every failure mode, so a blank file, the wrong template
        // and a genuine server fault were indistinguishable — and the only copy of the real reason
        // went to console.error, where neither the customer nor support would ever see it. Naming
        // the bad cell in DV-2844 took 11 days and an engineer with a copy of the file.
        const message = axios.isAxiosError(error)
          ? (error.response?.data as { message?: string } | undefined)?.message
          : undefined;
        stat.isError = true;
        // The fallback still covers exactly what it always described: no usable reason in the
        // response. That includes an aborted request, a network failure, and a WAF block, which
        // answers 403 with an empty body before the request reaches the service at all.
        stat.errorMessage =
          message?.trim() ||
          'Uploaded file is blank or in an incorrect template format. Please verify and re-upload.';
        stat.progress = 0;
        console.error(error);
        setFileStatus((prev) => [...prev]);
      });
  };

  const updateProgress = (event: AxiosProgressEvent, stat: fileDetails) => {
    if (event.total) {
      const percent = (event.loaded / event.total) * 100;
      stat.progress = Math.round(percent);
      setFileStatus((prev) => [...prev]);
    }
  };

  // -----------------------------------------------------------------------
  // Cancel / accordion
  // -----------------------------------------------------------------------

  const onCancel = (index: number) => {
    const removed: fileDetails = fileStatus.splice(index, 1)[0];
    removed.controller?.abort();
    setFileStatus((prev) => [...prev]);

    if (!isProperty) {
      setShowStep?.(false);
    }

    if (fileStatus.length === 0) {
      setDisabledForMode(false);
      return;
    }

    const allError = fileStatus.every((file) => file.isError);
    const hasNoError = fileStatus.some((file) => !file.isError);
    if (allError || !hasNoError) {
      setDisabledForMode(false);
    }
  };

  const toggleAccordion = () => setIsExpanded((open) => !open);

  // -----------------------------------------------------------------------
  // Aggregates
  // -----------------------------------------------------------------------

  const aggregateProgress = (files: fileDetails[]): allFileDetails => {
    let totalSuccessProgress = 0;
    let filesInSuccess = 0;
    let filesInError = 0;

    files.forEach((file) => {
      if (file.isError) {
        filesInError += 1;
      } else {
        totalSuccessProgress += file.progress;
        if (file.progress === 100) {
          filesInSuccess += 1;
        }
      }
    });

    const fileCount = files.length;
    return {
      fileProgress: fileCount ? totalSuccessProgress / fileCount : 0,
      filesInSuccess,
      errorProgress: fileCount ? (filesInError / fileCount) * 100 : 0,
      filesInError,
    };
  };

  const fileProgress = useMemo(() => aggregateProgress(fileStatus), [fileStatus]);
  const hasAnyError = fileStatus.some((file) => file.isError);
  const hasFiles = fileStatus.length > 0;
  const isDisabled = fileUploadDisabled || disabledUploadProperty;

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  return (
    <section
      onDragOver={() => setIsBeingDragged(true)}
      onDrop={() => setIsBeingDragged(false)}
    >
      <div
        {...getRootProps({
          className: cn(
            'group flex h-[150px] cursor-pointer flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed border-border bg-muted px-4 py-4 transition-colors lg:h-[140px]',
            'hover:border-info/40 hover:bg-info-soft',
            isBeingDragged && 'border-info bg-info-soft',
            isDisabled && 'pointer-events-none opacity-50',
          ),
          onDrop: (event) => {
            if (isDisabled) event.preventDefault();
          },
          onDragOver: (event) => {
            if (isDisabled) event.preventDefault();
          },
          onClick: (event) => {
            if (isDisabled) event.preventDefault();
          },
        })}
      >
        <input {...getInputProps()} aria-label="dropzone" disabled={isDisabled} />
        <UploadCloud
          className={cn(
            'h-10 w-10 text-muted-foreground transition-colors group-hover:text-info',
            isBeingDragged && 'text-info',
          )}
          aria-hidden="true"
        />
        <div className="flex items-center gap-1">
          <Typography variant="body-semibold">Drag &amp; drop file or</Typography>
          <Typography variant="body-semibold" className="text-info underline">
            Browse
          </Typography>
        </div>
        <Typography variant="small" className="text-center text-muted-foreground">
          {limits.helperCopy}
        </Typography>
      </div>

      <div ref={accordionRef}>
        {hasFiles && (
          <button
            className="mt-6 flex w-full cursor-pointer items-center gap-2 border-none bg-transparent p-0"
            aria-label="accordion"
            onClick={toggleAccordion}
          >
            <ChevronDown
              className={cn('h-4 w-4 transition-transform', !isExpanded && '-rotate-90')}
              aria-hidden="true"
            />
            <Typography variant="heading-md">File Uploaded</Typography>
            {hasAnyError ? (
              <CircleAlert className="ml-1 h-4 w-4 text-destructive" aria-hidden="true" />
            ) : (
              <CircleCheck className="ml-1 h-4 w-4 text-success" aria-hidden="true" />
            )}
          </button>
        )}
      </div>

      {!isExpanded && hasFiles && (
        <section className="mt-4">
          <div className="relative flex h-[18px] w-full overflow-hidden rounded-sm bg-secondary">
            <div
              className="flex h-full items-center justify-center bg-success text-[10px] text-white transition-all"
              style={{ width: `${fileProgress.fileProgress}%` }}
            >
              {fileProgress.filesInSuccess > 0 &&
                `${fileProgress.filesInSuccess} file uploaded`}
            </div>
            <div
              className="flex h-full items-center justify-center bg-destructive text-[10px] text-destructive-foreground transition-all"
              style={{ width: `${fileProgress.errorProgress}%` }}
            >
              {fileProgress.filesInError > 0 &&
                `${fileProgress.filesInError} files failed`}
            </div>
          </div>
        </section>
      )}

      {isExpanded && hasFiles && (
        <section className="mt-6 w-[70%] xl:w-full">
          {fileStatus.map((status, index) => (
            <FileProgress
              key={status.fileId}
              status={status}
              onCancel={() => onCancel(index)}
              isProperty={isProperty}
              inProgress={inProgress}
            />
          ))}
        </section>
      )}

      {isExpanded && hasFiles && inProgress && (
        <Alert variant="info" className="flex items-start gap-3">
          <Info className="h-5 w-5 shrink-0 text-info" aria-hidden="true" />
          <AlertDescription>
            <Typography variant="body">
              {`Processing the file may take a few moments depending on the number of records. To proceed with the ${limits.alertEntity}, you can wait on this page or navigate to another screen and check the status later in the`}
            </Typography>{' '}
            <Typography variant="body-semibold">
              {`${limits.alertSection} > Import history section.`}
            </Typography>
          </AlertDescription>
        </Alert>
      )}
    </section>
  );
});
